import type { ChannelMember } from '@src/api/shared/community/channelMemberApiClient';
import type { Attachment } from '@src/idb';
import { Editor } from '@tiptap/react';
import { EmojiClickData } from 'emoji-picker-react';
import {
  forwardRef,
  MutableRefObject,
  ReactNode,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';

import { EditorContent, EditorModals } from './components';
import { useTiptapDragDrop } from './hooks';
import { useIFrameModal } from './hooks/modals/useIFrameModal';
import { useLinkModal } from './hooks/modals/useLinkModal';
import { useTableModal } from './hooks/modals/useTableModal';
import { useVideoModal } from './hooks/modals/useVideoModal';
import { useTiptapEditorCore } from './hooks/useTiptapEditorCore';
import { useTiptapImageInteractions } from './hooks/useTiptapImageInteractions';
import { useTiptapKeyboardEvents } from './hooks/useTiptapKeyboardEvents';
import { useTiptapLinkInteractions } from './hooks/useTiptapLinkInteractions';
import { EditorFeatures } from './types';

export interface TiptapEditorCoreProps {
  features: EditorFeatures; // Feature configuration object defining what features are enabled
  value: string;
  onChange: (content: string) => void;

  height?: string | number;
  width?: string | number;
  containerHeight?: string | number;
  editorRef?: MutableRefObject<Editor | null>; // External editor ref for parent component access
  isInitFocus?: boolean;

  enableMentions?: boolean;
  placeholderText?: string;
  className?: string;

  // Community-specific props (optional)
  onSend?: () => void;
  attachments?: Attachment[];
  onAttachmentChange?: (attachments: Attachment[]) => void;
  channelCode?: string;
  channelName?: string;
  channelMembers?: ChannelMember[];
  channelVisibility?: number;
  isEditing?: boolean;
  editedChatId?: number;
  allowFileAttachment?: boolean;
  onCancelEdit?: () => void;
  currentUserId?: number;
  currentUserType?: string;
  isLoggedIn?: boolean;
  isCommunityEditor?: boolean;
  enablePasteFiltering?: boolean;
  removeTenantMentions?: boolean;
  onSelectEmoji?: (emojiObject: EmojiClickData) => void;
  onTriggerMention?: () => void;
  isMentionListActive?: boolean;
  onSelectFile?: (fileList: FileList) => void;

  // Tenant-specific props (optional)
  uploadFunction?: (file: File) => Promise<string>;
  onDragEnter?: (event: DragEvent) => void;
  onDragLeave?: (event: DragEvent) => void;
  onDrop?: (event: DragEvent) => void;

  onSendShortcut?: () => void;
  disabled?: boolean;
  enableLinkHoverTooltip?: boolean;
  enableLinkClickModal?: boolean;
  enableImageAltEdit?: boolean;

  // Callback to get eventHandlers for external use (e.g., toolbars)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onEventHandlersReady?: (eventHandlers: Record<string, any>) => void;

  children?: ReactNode;
}

/**
 * TiptapEditorCore
 * Core editor component that combines all hooks and features
 */
export const TiptapEditorCore = forwardRef<
  { getEditor: () => Editor | null },
  TiptapEditorCoreProps
>(
  (
    {
      features,
      value,
      onChange,
      height,
      width,
      containerHeight,
      editorRef: externalEditorRef,
      isInitFocus = false,
      enableMentions = false,
      placeholderText = '',
      enablePasteFiltering = false,
      removeTenantMentions = false,
      onDragEnter: _onDragEnter,
      onDragLeave: _onDragLeave,
      onDrop: _onDrop,
      onSendShortcut,
      disabled = false,
      enableLinkHoverTooltip = false,
      enableLinkClickModal = false,
      enableImageAltEdit = false,
      onEventHandlersReady,
      children,
      className = '',
      ..._rest
    },
    ref,
  ) => {
    const internalEditorRef = useRef<Editor | null>(null);

    const { editor } = useTiptapEditorCore({
      features,
      value,
      onChange,
      editorRef: externalEditorRef || internalEditorRef,
      isInitFocus,
      enableMentions,
      placeholderText,
      enablePasteFiltering,
      removeTenantMentions,
      channelName: _rest.channelName,
      channelMembers: _rest.channelMembers,
      currentUserId: _rest.currentUserId,
      currentUserType: _rest.currentUserType,
      isLoggedIn: _rest.isLoggedIn,
      isCommunityEditor: _rest.isCommunityEditor,
    });

    const videoModal = useVideoModal(editor);
    const iframeModal = useIFrameModal(editor);
    const tableModal = useTableModal(editor);
    const linkModal = useLinkModal(editor);

    const linkInteractions = useTiptapLinkInteractions({
      editor,
      linkModal,
      enableLinkHoverTooltip,
      enableLinkClickModal,
    });

    const imageInteractions = useTiptapImageInteractions({
      editor,
      enableImageAltEdit,
    });

    const eventHandlers = useMemo(
      () => ({
        ...linkModal,
        ...videoModal,
        ...iframeModal,
        ...tableModal,
        ...linkInteractions,
        ...imageInteractions,
      }),
      [linkModal, videoModal, iframeModal, tableModal, linkInteractions, imageInteractions],
    );

    useEffect(() => {
      if (onEventHandlersReady && Object.keys(eventHandlers).length > 0) {
        onEventHandlersReady(eventHandlers);
      }
    }, [eventHandlers, onEventHandlersReady]);

    const keyboardHandlers = useTiptapKeyboardEvents({
      editor,
      channelMembers: _rest.channelMembers || [],
      onSend: onSendShortcut,
      disabled: disabled,
    });

    const dragDropHandlers = useTiptapDragDrop({
      onDragEnter: _onDragEnter,
      onDragLeave: _onDragLeave,
      onDrop: _onDrop,
    });

    useImperativeHandle(ref, () => ({
      getEditor: () => editor,
    }));

    useEffect(() => {
      if (externalEditorRef) {
        externalEditorRef.current = editor;
      }
    }, [editor, externalEditorRef]);

    const modalStates = useMemo(
      () => ({
        isConvertUrlModalOpen: videoModal.isConvertUrlModalOpen,
        convertUrl: videoModal.convertUrl,
        convertUrlErrors: videoModal.convertUrlErrors,

        // IFrame modal state
        isIFrameCodeModalOpen: iframeModal.isIFrameCodeModalOpen,
        iFrameCode: iframeModal.iFrameCode,
        iFrameCodeErrors: iframeModal.iFrameCodeErrors,

        // Table modal state
        isInsertTableModalOpen: tableModal.isInsertTableModalOpen,
        tableRows: tableModal.tableRows,
        tableCols: tableModal.tableCols,
        tableErrors: tableModal.tableErrors,

        // Link modal state
        isLinkAddModalOpen: linkModal.isLinkAddModalOpen,
        linkAddModalParams: linkModal.linkAddModalParams,
        linkAddModalInitialParams: linkModal.linkAddModalInitialParams,
        linkUrlErrors: linkModal.linkUrlErrors,

        // Link interaction state
        isLinkHoverTooltipOpen: linkInteractions.isLinkHoverTooltipOpen,
        linkHoverTooltipPosition: linkInteractions.linkHoverTooltipPosition,
        linkHoverTooltipUrl: linkInteractions.linkHoverTooltipUrl,
        isLinkEditPopupOpen: linkInteractions.isLinkEditPopupOpen,

        // Image interaction state
        isImageAltPopupOpen: imageInteractions.isImageAltPopupOpen,
        imageAltPopupPosition: imageInteractions.imageAltPopupPosition,
        currentImageAlt: imageInteractions.currentImageAlt,
      }),
      [videoModal, iframeModal, tableModal, linkModal, linkInteractions, imageInteractions],
    );

    return (
      <div
        className='flex size-full flex-col'
        style={{
          height: containerHeight || height,
          width,
        }}
        {...dragDropHandlers}
      >
        {children}

        <EditorContent
          editor={editor}
          height={typeof height === 'number' ? `${height}px` : height || '300px'}
          keyboardHandlers={keyboardHandlers}
          className={className}
        />

        <EditorModals
          editor={editor}
          features={features}
          modalStates={modalStates}
          eventHandlers={eventHandlers}
          enableLinkHoverTooltip={enableLinkHoverTooltip}
          enableLinkClickModal={enableLinkClickModal}
          enableImageAltEdit={enableImageAltEdit}
        />
      </div>
    );
  },
);

TiptapEditorCore.displayName = 'TiptapEditorCore';
